import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useStoreProfileById } from "@/hooks/useStoreProfile";
import { StoreSettingsForm } from "@/components/store-management/StoreSettingsForm";
import { Loader2, Store } from "lucide-react";
import { DashboardLayout } from '@/components/florist-dashboard/DashboardLayout';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function StoreSettings() {
  const { user, isFlorist, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const {
    storeProfile,
    isLoading,
    error,
    updateStoreProfile,
    activateStore
  } = useStoreProfileById({
    floristId: user?.id || "",
    enabled: !authLoading && !!user && !!isFlorist
  });

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    if (!isFlorist) {
      navigate("/");
      return;
    }
  }, [user, isFlorist, authLoading, navigate]);

  const handleSaveProfile = async (data: UpdateStoreProfileInput) => {
    try {
      console.log("Saving profile with data:", {
        business_hours: data.business_hours,
        store_name: data.store_name
      });
      
      await updateStoreProfile(data);
      console.log("Profile successfully updated");
      
      // Show success toast
      toast.success("Settings saved successfully!", {
        description: "Your store settings have been updated."
      });
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate("/florist-dashboard");
      }, 1000);
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save settings", {
        description: "Please try again or contact support if the problem persists."
      });
    }
  };

  if (authLoading || (isLoading && !storeProfile)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-brand-primary">Store Settings</h1>
            <p className="text-brand-primary/70 mt-1">
              Manage your store profile and settings
            </p>
          </div>
          <div className="bg-red-50 p-6 rounded-lg border border-red-200 text-center">
            <h2 className="text-lg font-medium text-red-600 mb-2">Error</h2>
            <p className="text-red-600">
              Failed to load store settings. Please try again later.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Store Settings</h1>
          <p className="text-brand-primary/70 mt-1">
            Manage your store profile and business hours
          </p>
        </div>

        <div className="bg-white rounded-lg border border-brand-primary/10 shadow-sm p-6">
          {storeProfile ? (
            <StoreSettingsForm
              initialData={storeProfile}
              onSave={handleSaveProfile}
              isLoading={isLoading}
            />
          ) : (
            <div className="text-center py-12 bg-[#967F81]/5 rounded-lg border border-brand-primary/10">
              <div className="bg-[#967F81]/5 p-4 rounded-full mb-4 mx-auto w-fit">
                <Store className="h-8 w-8 text-brand-primary/60" />
              </div>
              <p className="text-brand-primary/70 font-medium mb-1">No store profile found</p>
              <p className="text-sm text-brand-primary/50">
                You need to create a store profile to continue.
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
} 